/**
 * Map capture — screenshot the current map view for intelligence reports.
 *
 * Captures both the MapLibre canvas and any deck.gl overlay, composites them,
 * adds a timestamp + coordinate watermark, and downloads as PNG.
 */

import { getMap } from '@/map/engine';

/** Format current UTC timestamp for watermark. */
function utcTimestamp(): string {
  const now = new Date();
  return now.toISOString().replace('T', ' ').replace(/\.\d+Z$/, 'Z');
}

/** Get the current map viewport description for the watermark. */
function viewportDescription(): string {
  const map = getMap();
  if (!map) return '';
  const center = map.getCenter();
  const zoom = map.getZoom();
  return `${center.lat.toFixed(4)}°, ${center.lng.toFixed(4)}° Z${zoom.toFixed(1)}`;
}

/** Capture the map as a PNG and trigger download. */
export async function captureMap(): Promise<void> {
  const map = getMap();
  if (!map) return;

  const mapCanvas = map.getCanvas();
  const width = mapCanvas.width;
  const height = mapCanvas.height;

  // Create composite canvas
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Draw MapLibre base map
  ctx.drawImage(mapCanvas, 0, 0);

  // Draw deck.gl overlay if present
  const deckCanvas = document.getElementById('deck-canvas') as HTMLCanvasElement | null;
  if (deckCanvas && deckCanvas.style.display !== 'none') {
    ctx.drawImage(deckCanvas, 0, 0);
  }

  // Draw any additional overlay canvases (e.g., labels)
  const container = mapCanvas.parentElement;
  if (container) {
    const overlayCanvases = container.querySelectorAll('canvas:not(.maplibregl-canvas)');
    overlayCanvases.forEach(c => {
      if (c !== deckCanvas && c instanceof HTMLCanvasElement) {
        ctx.drawImage(c, 0, 0);
      }
    });
  }

  // Add watermark bar at bottom
  const dpr = window.devicePixelRatio || 1;
  const barHeight = 28 * dpr;
  const fontSize = 11 * dpr;

  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
  ctx.fillRect(0, height - barHeight, width, barHeight);

  ctx.fillStyle = '#999999';
  ctx.font = `${fontSize}px "JetBrains Mono", monospace`;
  ctx.textBaseline = 'middle';

  const timestamp = utcTimestamp();
  const viewport = viewportDescription();
  const leftText = `SENTINEL  •  ${timestamp}`;
  const rightText = viewport;

  const padding = 12 * dpr;
  ctx.fillText(leftText, padding, height - barHeight / 2);

  const rightWidth = ctx.measureText(rightText).width;
  ctx.fillText(rightText, width - rightWidth - padding, height - barHeight / 2);

  // Download
  const blob = await new Promise<Blob | null>(resolve =>
    canvas.toBlob(resolve, 'image/png'),
  );
  if (!blob) return;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `sentinel-capture-${Date.now()}.png`;
  a.click();
  URL.revokeObjectURL(url);

  // Flash feedback
  const flashEl = document.querySelector('.capture-tool-btn');
  if (flashEl) {
    flashEl.classList.add('active');
    setTimeout(() => flashEl.classList.remove('active'), 600);
  }
}
